import React, { useMemo } from 'react';
import { Card, Descriptions, Table, Button, Space, Popconfirm, App, Typography } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supplierQuotationService, type SupplierQuotationLine } from '@/services/supplierQuotation.service';
import { LineSpecificationsText, isSpecDeviation } from '@/components/procurement/LineSpecifications';
import { supplierInquiryService } from '@/services/supplierInquiry.service';
import { bidAnalysisService } from '@/services/bidAnalysis.service';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import { projectLabel } from '@/utils/projectDisplay';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_SUPPLIER_QUOTATION } from '@/constants/document-entity-types';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

const SupplierQuotationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { data: departments = [] } = useDepartmentsForSelection();

  const departmentLabel = (deptId?: string | null) => {
    if (!deptId) return '—';
    const d = departments.find((x) => x.id === deptId);
    return d ? `${d.department_code} - ${d.department_name}` : deptId;
  };

  const { data: quotation, isLoading } = useQuery({
    queryKey: ['supplier-quotation', id],
    queryFn: () => supplierQuotationService.getById(id!),
    enabled: !!id,
  });

  const inquiryId = quotation?.supplier_inquiry_id;

  const { data: inquiry } = useQuery({
    queryKey: ['supplier-inquiry', inquiryId],
    queryFn: () => supplierInquiryService.getById(inquiryId!),
    enabled: !!inquiryId,
    staleTime: 60000,
  });
  const requiredSpecByInquiryLine = useMemo(
    () => new Map((inquiry?.lines ?? []).map((l) => [l.id, l.specifications ?? null])),
    [inquiry?.lines]
  );

  const { data: linkedBidAnalyses = [] } = useQuery({
    queryKey: ['bid-analyses', 'by-inquiry', inquiryId],
    queryFn: () => bidAnalysisService.list({ supplier_inquiry_id: inquiryId! }),
    enabled: !!inquiryId,
    staleTime: 30000,
  });

  const { data: linkedPurchaseOrders = [] } = useQuery({
    queryKey: ['purchase-orders', 'by-inquiry', inquiryId],
    queryFn: () => purchaseOrderService.list({ supplier_inquiry_id: inquiryId! }),
    enabled: !!inquiryId,
    staleTime: 30000,
  });

  const posForThisQuotation = useMemo(
    () =>
      (linkedPurchaseOrders as any[]).filter(
        (po) =>
          po.supplier_quotation_id === id ||
          (po.bidAnalysisSources || []).some((s: any) =>
            (s.supplier_quotation_ids || []).includes(id)
          )
      ),
    [linkedPurchaseOrders, id]
  );

  const deleteMutation = useMutation({
    mutationFn: () => supplierQuotationService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-quotations'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-quotations-by-inquiry'] });
      message.success('Quotation deleted');
      navigate('/scm/procurement/supplier-quotations');
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    },
  });

  if (!id || (isLoading && !quotation)) return null;
  if (!quotation) return <Card><p>Quotation not found.</p></Card>;

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/supplier-quotations')} style={{ marginBottom: 16 }}>
        Back
      </Button>
      <Card
        title={`Quotation ${quotation.quotation_number ?? quotation.id}`}
        extra={
          <Space>
            <Popconfirm
              title="Delete this quotation?"
              description="Not allowed if used in bid analysis or on a purchase order."
              okText="Delete"
              okButtonProps={{ danger: true }}
              disabled={deleteMutation.isPending}
              onConfirm={() => deleteMutation.mutate()}
            >
              <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <Typography.Text strong>Document links: </Typography.Text>
          <Space wrap size="small">
            {quotation.supplier_inquiry_id ? (
              <DocumentRefLink
                to={`/scm/procurement/supplier-inquiries/${quotation.supplier_inquiry_id}`}
                number={inquiry?.inquiry_number}
                id={quotation.supplier_inquiry_id}
                name="Inquiry"
              />
            ) : null}
            {linkedBidAnalyses.map((ba) => (
              <DocumentRefLink
                key={ba.id}
                to={`/scm/procurement/bid-analyses/${ba.id}`}
                number={ba.analysis_number}
                id={ba.id}
              />
            ))}
            {posForThisQuotation.map((po: any) => (
              <DocumentRefLink
                key={po.id}
                to={`/scm/procurement/purchase-orders/${po.id}`}
                number={po.po_number}
                id={po.id}
              />
            ))}
          </Space>
        </div>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Supplier">{(quotation.supplier as any)?.supplier_name ?? quotation.supplier_id}</Descriptions.Item>
          <Descriptions.Item label="Status">{quotation.status}</Descriptions.Item>
          <Descriptions.Item label="Department">
            {quotation.status === 'Draft' ? (
              <DepartmentSelect
                style={{ width: 280 }}
                value={quotation.department_id || inquiry?.department_id || undefined}
                onChange={async (v) => {
                  try {
                    await supplierQuotationService.update(id!, { department_id: v as string });
                    message.success('Department updated');
                    queryClient.invalidateQueries({ queryKey: ['supplier-quotation', id] });
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || 'Failed to update department');
                  }
                }}
              />
            ) : (
              departmentLabel(quotation.department_id || inquiry?.department_id)
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Quotation date">{quotation.quotation_date ? dayjs(quotation.quotation_date).format('DD MMM YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Validity date">{quotation.validity_date ? dayjs(quotation.validity_date).format('DD MMM YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Supplier ref" span={2}>{quotation.supplier_quotation_ref || '-'}</Descriptions.Item>
          <Descriptions.Item label="Payment terms" span={2}>{quotation.payment_terms || '-'}</Descriptions.Item>
          <Descriptions.Item label="Remarks" span={2}>{quotation.remarks || '-'}</Descriptions.Item>
        </Descriptions>
        <h4 style={{ marginTop: 16 }}>Lines</h4>
        <Table
          size="small"
          rowKey="id"
          dataSource={quotation.lines ?? []}
          columns={[
            { title: '#', dataIndex: 'line_number', width: 50 },
            { title: 'Item', dataIndex: 'item_code', width: 100 },
            {
              title: 'Description',
              dataIndex: 'item_description',
              render: (desc: string, row: SupplierQuotationLine) => {
                const required = requiredSpecByInquiryLine.get(row.supplier_inquiry_line_id);
                return (
                  <>
                    <div>{desc}</div>
                    <LineSpecificationsText value={required} label="Required spec" />
                    {row.brand_offered ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        Brand offered: {row.brand_offered}
                      </Typography.Text>
                    ) : null}
                    <LineSpecificationsText
                      value={row.compliance_notes}
                      label="Offered spec / deviations"
                      deviation={isSpecDeviation(required, row.compliance_notes)}
                    />
                  </>
                );
              },
            },
            { title: 'Project', key: 'project', width: 120, render: (_: unknown, row: any) => projectLabel(row) },
            { title: 'Qty', dataIndex: 'quoted_quantity', width: 80, align: 'right' },
            { title: 'UOM', dataIndex: 'uom', width: 60 },
            { title: 'Unit price', dataIndex: 'unit_price', width: 100, align: 'right', render: (v: number) => (v != null ? Number(v).toFixed(2) : '-') },
            { title: 'Disc %', dataIndex: 'discount_percent', width: 70, align: 'right' },
            { title: 'Line total', dataIndex: 'line_total', width: 100, align: 'right', render: (v: number) => (v != null ? Number(v).toFixed(2) : '-') },
            { title: 'Lead days', dataIndex: 'lead_time_days', width: 80 },
            { title: 'Delivery', dataIndex: 'promised_delivery_date', width: 110, render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-') },
          ]}
          pagination={false}
        />
      </Card>

      <ProcurementAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_QUOTATION}
        entityId={id}
        queryKeyPrefix={['documents', 'supplier-quotation']}
      />
    </div>
  );
};

export default SupplierQuotationDetailPage;
